import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Leaf, Home as HomeIcon, ListChecks, Heart, BookOpen, Newspaper, ChevronDown, ShoppingBag, ShoppingCart, LogIn, LogOut, User, Crown, UserPlus, Menu, X, Sparkles, Zap, Store } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

// Social Media Icons
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const YouTubeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const SocialLinks = () => (
  <div className="flex items-center gap-3">
    <a
      href="https://instagram.com/elite_herbalist"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-500 hover:text-pink-500 transition-colors"
      aria-label="Instagram"
    >
      <InstagramIcon />
    </a>
    <a
      href="https://tiktok.com/@eliteherbalist"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-500 hover:text-black transition-colors"
      aria-label="TikTok"
    >
      <TikTokIcon />
    </a>
    <a
      href="https://youtube.com/@EliteHerbalist"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-500 hover:text-red-500 transition-colors"
      aria-label="YouTube"
    >
      <YouTubeIcon />
    </a>
  </div>
);

const Header = () => {
  const location = useLocation();
  const isQuestionnaire = location.pathname === "/questionnaire";
  const { user, isAuthenticated, isPremium, logout, openAuth, setPaywallModalOpen } = useAuth();
  const { count } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Don't show header on questionnaire page (focus mode)
  if (isQuestionnaire) return null;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center group-hover:scale-105 transition-transform">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-playfair text-xl font-bold text-primary whitespace-nowrap max-[429px]:hidden">Elite Herbalist</span>
            </Link>
          </div>

          <nav className="hidden xl:flex items-center space-x-3 whitespace-nowrap">
            <Link
              to="/store"
              className="text-gray-600 hover:text-primary transition-colors text-sm font-medium"
              data-testid="nav-shop-link"
            >
              Herb Shop
            </Link>
            <Link
              to="/herbs"
              className="text-gray-600 hover:text-primary transition-colors text-sm font-medium"
              data-testid="nav-encyclopedia-link"
            >
              Encyclopedia
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 text-gray-600 hover:text-primary transition-colors text-sm font-medium outline-none"
                data-testid="nav-explore-dropdown"
              >
                Wellness <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem asChild data-testid="nav-how-it-works-link">
                  <Link to="/#how-it-works" className="flex items-center gap-2 cursor-pointer">
                    <ListChecks className="w-4 h-4 text-primary" /> How It Works
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-womens-wellness-link">
                  <Link to={isPremium ? "/womens-hormone-guide" : "/#vitality-tests"} className="flex items-center gap-2 cursor-pointer">
                    <Heart className="w-4 h-4 text-primary" /> Women's Wellness
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-mens-wellness-link">
                  <Link to={isPremium ? "/mens-vitality-guide" : "/#vitality-tests"} className="flex items-center gap-2 cursor-pointer">
                    <Zap className="w-4 h-4 text-primary" /> Men's Wellness
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="explore-news-link">
                  <Link to="/news" className="flex items-center gap-2 cursor-pointer">
                    <Newspaper className="w-4 h-4 text-primary" /> Daily News
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="explore-infuso-link">
                  <Link to="/shop" className="flex items-center gap-2 cursor-pointer">
                    <ShoppingBag className="w-4 h-4 text-primary" /> Shop Infuso
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/white-label"
              className="inline-flex items-center gap-1.5 border border-emerald-200 bg-emerald-50/70 text-primary hover:bg-emerald-100 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors whitespace-nowrap"
              data-testid="nav-store-owners-link"
            >
              <Store className="w-3.5 h-3.5" />
              For Store Owners
            </Link>
            <Link
              to="/questionnaire"
              className="flex items-center gap-1.5 bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-700 hover:to-emerald-800 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md shadow-emerald-600/20 transition-all uppercase tracking-wide"
              data-testid="nav-free-assessment-btn"
            >
              <Sparkles className="w-4 h-4" />
              Free Assessment
            </Link>
            <Link
              to="/cart"
              className="relative text-gray-600 hover:text-primary transition-colors p-1"
              data-testid="header-cart-link"
              aria-label="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="header-cart-count">
                  {count}
                </span>
              )}
            </Link>
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {isPremium ? (
                  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 px-3 py-1 rounded-full text-xs font-bold border border-amber-300"
                        data-testid="premium-badge">
                    <Crown className="w-3 h-3" /> PREMIUM
                  </span>
                ) : (
                  <button
                    onClick={() => setPaywallModalOpen(true)}
                    data-testid="header-upgrade-btn"
                    className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md"
                  >
                    <Crown className="w-3.5 h-3.5" /> Upgrade
                  </button>
                )}
                <Link to="/account" className="flex items-center gap-2 text-xs text-gray-600 border-l border-gray-200 pl-3 hover:text-primary transition-colors" data-testid="header-account-link">
                  <User className="w-3.5 h-3.5 text-primary" />
                  <span className="max-w-[140px] truncate" data-testid="header-user-email">
                    {user?.email}
                  </span>
                </Link>
                <button onClick={logout}
                  className="text-gray-400 hover:text-rose-600 transition-colors"
                  data-testid="logout-btn" aria-label="Sign out">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => openAuth("login")}
                  className="text-gray-700 hover:text-primary px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-1"
                  data-testid="header-login-btn">
                  <LogIn className="w-4 h-4" /> Log In
                </button>
                <button onClick={() => openAuth("signup")}
                  className="bg-primary text-white px-5 py-2 rounded-full hover:bg-primary/90 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5 text-sm font-semibold"
                  data-testid="header-signup-btn">
                  <UserPlus className="w-4 h-4" /> Sign Up
                </button>
              </div>
            )}
          </nav>

          <div className="flex xl:hidden items-center gap-2">
            <Link
              to="/questionnaire"
              className="flex items-center gap-1 bg-gradient-to-r from-primary to-emerald-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-sm whitespace-nowrap"
              data-testid="mobile-nav-assessment-btn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Free Assessment
            </Link>
            <Link
              to="/cart"
              className="relative text-gray-600 hover:text-primary transition-colors p-1"
              data-testid="mobile-cart-link"
              aria-label="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="mobile-cart-count">
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="text-gray-700 hover:text-primary transition-colors p-1"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-1">
            <Link to="/" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-home-link">
              <HomeIcon className="w-5 h-5" /> Home
            </Link>
            <Link to="/store" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-shop-menu-link">
              <ShoppingBag className="w-5 h-5" /> Herb Shop
            </Link>
            <Link to="/herbs" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-encyclopedia-link">
              <BookOpen className="w-5 h-5" /> Encyclopedia
            </Link>
            <p className="px-3 pt-2 pb-1 text-xs font-bold text-gray-400 uppercase tracking-widest">Wellness</p>
            <Link to="/#how-it-works" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-how-it-works-link">
              <ListChecks className="w-5 h-5" /> How It Works
            </Link>
            <Link to={isPremium ? "/womens-hormone-guide" : "/#vitality-tests"} className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-womens-wellness-link">
              <Heart className="w-5 h-5" /> Women's Wellness
            </Link>
            <Link to={isPremium ? "/mens-vitality-guide" : "/#vitality-tests"} className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-mens-wellness-link">
              <Zap className="w-5 h-5" /> Men's Wellness
            </Link>
            <p className="px-3 pt-2 pb-1 text-xs font-bold text-gray-400 uppercase tracking-widest">More</p>
            <Link to="/news" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-news-link">
              <Newspaper className="w-5 h-5" /> Daily News
            </Link>
            <Link to="/shop" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-infuso-link">
              <ShoppingBag className="w-5 h-5" /> Shop Infuso
            </Link>
            <Link to="/white-label" className="flex items-center gap-3 px-3 py-3 rounded-lg text-primary font-semibold bg-emerald-50/60 hover:bg-emerald-100 transition-colors" data-testid="mobile-store-owners-link">
              <Store className="w-5 h-5" /> For Store Owners
            </Link>
            <Link to="/cart" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-cart-menu-link">
              <ShoppingCart className="w-5 h-5" /> Cart {count > 0 && <span className="bg-primary text-white text-xs font-bold rounded-full px-2 py-0.5">{count}</span>}
            </Link>

            <div className="border-t border-gray-100 my-2 pt-2">
              {isAuthenticated ? (
                <>
                  {isPremium ? (
                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 px-3 py-1 rounded-full text-xs font-bold border border-amber-300 ml-3" data-testid="mobile-premium-badge">
                      <Crown className="w-3 h-3" /> PREMIUM
                    </span>
                  ) : (
                    <button
                      onClick={() => { setMobileOpen(false); setPaywallModalOpen(true); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-amber-700 font-semibold hover:bg-amber-50 transition-colors"
                      data-testid="mobile-upgrade-btn"
                    >
                      <Crown className="w-5 h-5" /> Upgrade to Premium
                    </button>
                  )}
                  <Link to="/account" className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-primary transition-colors" data-testid="mobile-account-link">
                    <User className="w-5 h-5" /> <span className="truncate">{user?.email}</span>
                  </Link>
                  <button onClick={() => { setMobileOpen(false); logout(); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                    data-testid="mobile-logout-btn">
                    <LogOut className="w-5 h-5" /> Sign Out
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-3 py-2">
                  <button onClick={() => { setMobileOpen(false); openAuth("login"); }}
                    className="flex-1 border border-gray-200 text-gray-700 hover:text-primary px-4 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5"
                    data-testid="mobile-login-btn">
                    <LogIn className="w-4 h-4" /> Log In
                  </button>
                  <button onClick={() => { setMobileOpen(false); openAuth("signup"); }}
                    className="flex-1 bg-primary text-white px-4 py-2.5 rounded-full hover:bg-primary/90 transition-all shadow-sm flex items-center justify-center gap-1.5 text-sm font-semibold"
                    data-testid="mobile-signup-btn">
                    <UserPlus className="w-4 h-4" /> Sign Up
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-3 px-3">
              <SocialLinks />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export { SocialLinks, InstagramIcon, TikTokIcon, YouTubeIcon };
export default Header;